import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../api';
import type { User } from '../types';

interface AuthContextValue {
  user: User | null;
  token: string | null;
  loading: boolean;
  /** Resolves with a challenge when the account has 2FA, instead of signing in. */
  login: (email: string, password: string) => Promise<{ mfaChallenge?: string }>;
  completeMfaLogin: (challenge: string, code: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(() => localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    navigate('/login');
  }, [navigate]);

  // On mount: if token exists, fetch current user
  useEffect(() => {
    const stored = localStorage.getItem('token');
    if (!stored) {
      setLoading(false);
      return;
    }
    auth
      .me()
      .then((u) => {
        setUser(u);
        setToken(stored);
      })
      .catch(() => {
        localStorage.removeItem('token');
        setToken(null);
      })
      .finally(() => setLoading(false));
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const result = await auth.login(email, password);

    // With 2FA on, the password step yields only a challenge — no session yet.
    if (result.mfaRequired && result.challenge) {
      return { mfaChallenge: result.challenge };
    }

    if (!result.token || !result.user) {
      throw new Error('Unerwartete Antwort beim Anmelden.');
    }
    localStorage.setItem('token', result.token);
    setToken(result.token);
    setUser(result.user);
    return {};
  }, []);

  const completeMfaLogin = useCallback(async (challenge: string, code: string) => {
    const result = await auth.verifyMfa(challenge, code);
    localStorage.setItem('token', result.token);
    setToken(result.token);
    setUser(result.user);
  }, []);

  return (
    <AuthContext.Provider value={{ user, token, loading, login, completeMfaLogin, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
